// src/router/index.js
import Vue from 'vue'
import VueRouter from 'vue-router'
import store from '@/store'
import { permissionForPath } from '@/menu'

Vue.use(VueRouter)

// vue-router 3.x：重复跳转到当前路由会抛 NavigationDuplicated（未捕获时整页红屏）。
// 后台的常见场景：多个请求同时 401 都跳登录、点击当前所在的菜单项。
// 这类重复导航属于正常情况，统一吞掉；其它导航错误照常抛出。
function silenceDuplicateNavigation(original) {
  return function (location, onResolve, onReject) {
    if (onResolve || onReject) return original.call(this, location, onResolve, onReject)
    return original.call(this, location).catch(error => {
      if (error && error.name === 'NavigationDuplicated') return error
      throw error
    })
  }
}
VueRouter.prototype.push = silenceDuplicateNavigation(VueRouter.prototype.push)
VueRouter.prototype.replace = silenceDuplicateNavigation(VueRouter.prototype.replace)

const routes = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/Login.vue'),
    meta: { public: true }
  },
  {
    path: '/',
    component: () => import('@/views/Layout.vue'),
    redirect: '/dashboard',
    children: [
      { path: 'dashboard', name: 'Dashboard', component: () => import('@/views/Dashboard.vue'),
        meta: { title: '仪表盘', icon: 'el-icon-s-home' } },
      { path: 'activities', name: 'ActivityList', component: () => import('@/views/ActivityList.vue'),
        meta: { title: '课程管理', icon: 'el-icon-document' } },
      { path: 'activities/create', name: 'ActivityCreate', component: () => import('@/views/ActivityEdit.vue'),
        meta: { title: '创建活动' } },
      { path: 'activities/:id/edit', name: 'ActivityEdit', component: () => import('@/views/ActivityEdit.vue'),
        meta: { title: '编辑活动' } },
      { path: 'timetable', name: 'Timetable', component: () => import('@/views/Timetable.vue'),
        meta: { title: '课程表', icon: 'el-icon-date' } },
      { path: 'schools', name: 'SchoolList', component: () => import('@/views/ContentList.vue'), props: { type: 'school' }, meta: { title: '学校资料' } },
      { path: 'schools/create', redirect: '/schools' },
      { path: 'schools/:id/edit', name: 'SchoolEdit', component: () => import('@/views/ContentEdit.vue'), props: { type: 'school' }, meta: { title: '编辑学校资料' } },
      { path: 'schools/:id', name: 'SchoolDetail', component: () => import('@/views/ContentDetail.vue'), props: { type: 'school' }, meta: { title: '学校资料详情' } },
      { path: 'mentors', name: 'MentorList', component: () => import('@/views/ContentList.vue'), props: { type: 'mentor' }, meta: { title: '导师资料' } },
      { path: 'mentors/create', name: 'MentorCreate', component: () => import('@/views/ContentEdit.vue'), props: { type: 'mentor' }, meta: { title: '新增导师资料' } },
      { path: 'mentors/:id/edit', name: 'MentorEdit', component: () => import('@/views/ContentEdit.vue'), props: { type: 'mentor' }, meta: { title: '编辑导师资料' } },
      { path: 'mentors/:id', name: 'MentorDetail', component: () => import('@/views/ContentDetail.vue'), props: { type: 'mentor' }, meta: { title: '导师资料详情' } },
      { path: 'course-categories', name: 'CategoryList', component: () => import('@/views/CategoryList.vue'), meta: { title: '课程分类' } },
      { path: 'course-categories/create', name: 'CategoryCreate', component: () => import('@/views/CategoryEdit.vue'), meta: { title: '新增课程分类' } },
      { path: 'course-categories/:id/edit', name: 'CategoryEdit', component: () => import('@/views/CategoryEdit.vue'), meta: { title: '编辑课程分类' } },
      { path: 'course-categories/:id', name: 'CategoryDetail', component: () => import('@/views/CategoryDetail.vue'), meta: { title: '课程分类详情' } },
      { path: 'registrations', name: 'RegistrationList', component: () => import('@/views/RegistrationList.vue'),
        meta: { title: '报名管理', icon: 'el-icon-tickets' } },
      { path: 'orders', name: 'OrderList', component: () => import('@/views/OrderList.vue'),
        meta: { title: '订单管理', icon: 'el-icon-money' } },
      { path: 'users', name: 'UserList', component: () => import('@/views/UserList.vue'),
        meta: { title: '用户管理', icon: 'el-icon-user' } },
      { path: 'news', name: 'NewsList', component: () => import('@/views/NewsList.vue'),
        meta: { title: '新闻公告', icon: 'el-icon-reading' } },
      { path: 'news/create', name: 'NewsCreate', component: () => import('@/views/NewsEdit.vue'),
        meta: { title: '发布公告' } },
      { path: 'news/:id/edit', name: 'NewsEdit', component: () => import('@/views/NewsEdit.vue'),
        meta: { title: '编辑公告' } },
      { path: 'help-config', name: 'HelpConfig', component: () => import('@/views/HelpConfig.vue'),
        meta: { title: '帮助与联系', icon: 'el-icon-chat-dot-round' } },
      { path: 'agreement-config', name: 'AgreementConfig', component: () => import('@/views/AgreementConfig.vue'),
        meta: { title: '协议配置', icon: 'el-icon-document-checked' } },
      { path: 'admins', name: 'AdminList', component: () => import('@/views/AdminList.vue'),
        meta: { title: '管理员管理', icon: 'el-icon-setting', permission: 'admin-management' } },
      { path: 'profile', name: 'Profile', component: () => import('@/views/Profile.vue'),
        meta: { title: '个人设置' } }
    ]
  }
]

// 管理后台使用 Hash 路由，刷新详情/编辑等深层页面时无需服务器配置 History 回退。
const router = new VueRouter({ mode: 'hash', routes })

router.beforeEach((to, from, next) => {
  const token = store.state.auth.token
  if (to.meta.public) {
    next()
  } else if (!token) {
    next('/login')
  } else {
    const required = to.meta.permission || permissionForPath(to.path)
    if (required && required !== 'dashboard' && !store.getters['auth/can'](required)) {
      // 无权限：提示并跳回仪表盘
      if (to.path !== '/dashboard') {
        Vue.prototype.$message ? Vue.prototype.$message.warning('您没有访问该页面的权限') : null
        next('/dashboard')
      } else {
        next()
      }
      return
    }
    document.title = to.meta.title ? to.meta.title + ' - 课程管理后台' : '课程管理后台'
    next()
  }
})

export default router

